import { Link } from "react-router-dom";
import { Sun, ClipboardList, Dumbbell, HeartPulse, Flame, LineChart, Watch, CheckCircle2, ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";

/** /player-guide — public, shareable Player Instructions & Daily Use guide. */
export default function PlayerGuide() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="player-guide-page">
      <SiteNav />

      <section className="pt-32 pb-12 max-w-4xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-cyan-300 mb-3">Player Guide</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Your daily playbook.<br /><span className="text-white/50">Two minutes a day. A career-long record.</span>
        </h1>
        <p className="mt-5 text-white/65 max-w-2xl leading-relaxed">
          BioHacker works when you feed it. Log daily, train what it tells you, and every verified rep
          builds your permanent performance profile — the one recruiters and next-level coaches can trust.
        </p>
      </section>

      {/* Getting started */}
      <section className="max-w-4xl mx-auto px-6 pb-12">
        <h2 className="font-display font-black text-2xl mb-4">First-time setup (5 minutes)</h2>
        <div className="space-y-3">
          {[
            ["Create your account", "Use the invite link from your coach (team invite or Fusion link). Set a password you'll remember."],
            ["Upload your profile photo", "A clear, front-facing headshot — it powers your profile and (if your team runs Fusion) your fundraising page and AI video."],
            ["Enter your baselines", "Height, weight, position, and any test numbers your coach hasn't already logged (10-yd split, 30.6-Yard Home-to-First, 60-yd, jumps)."],
            ["Connect your wearable (optional)", "Hume band or Apple Health in the Portal → richer recovery and readiness scores without extra typing."],
          ].map(([t, d], i) => (
            <div key={t} className="ink-card rounded-2xl p-5 flex gap-4 items-start">
              <div className="h-8 w-8 rounded-full bg-cyan-400 text-[#0b1424] font-black flex items-center justify-center shrink-0">{i + 1}</div>
              <div>
                <div className="font-display font-black">{t}</div>
                <p className="text-sm text-white/60 mt-1">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Daily rhythm */}
      <section className="max-w-4xl mx-auto px-6 pb-12">
        <h2 className="font-display font-black text-2xl mb-4">Your daily rhythm</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { icon: Sun, time: "MORNING · 60 seconds", t: "Submit your Daily Log", d: "Sleep, soreness, energy, stress — straight from your Portal home. This drives your readiness score and tells your coach whether to push you or protect you today. No log = the system is blind." },
            { icon: ClipboardList, time: "BEFORE PRACTICE", t: "Check Today's Plan", d: "Portal → Today's Plan shows what the system recommends for YOUR body today, based on readiness and workload. Green means go. Amber means modify. Red means recover." },
            { icon: Dumbbell, time: "PRE-TRAINING", t: "Run your Warm-Up", d: "Portal → Warm-Up gives your position-specific activation routine. Check off each step — completion is tracked and correlates with fewer soft-tissue issues." },
            { icon: HeartPulse, time: "AFTER TRAINING", t: "Log recovery honestly", d: "Report how the session actually felt. Honest data protects you — inflated numbers get athletes hurt. Your recovery tools live in Portal → Recovery." },
            { icon: LineChart, time: "WEEKLY", t: "Review your History", d: "Portal → History shows your trends: readiness, workload, test results, personal bests. Watch your 30.6-Yard Home-to-First and jump numbers move — that's your development, in writing." },
            { icon: Flame, time: "DURING A FUSION EVENT", t: "Work your campaign", d: "Portal → Fusion: approve your photo, publish your AI video, and share your page weekly. After event day, your verified best hit posts to your permanent profile automatically." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.t} className="ink-card rounded-2xl p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-cyan-300">
                  <Icon className="h-4 w-4" /> {c.time}
                </div>
                <div className="font-display font-black mt-2">{c.t}</div>
                <p className="text-sm text-white/60 mt-1.5 leading-relaxed">{c.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Rules */}
      <section className="max-w-4xl mx-auto px-6 pb-12">
        <h2 className="font-display font-black text-2xl mb-4">Five rules that make it work</h2>
        <div className="ink-card rounded-2xl p-6 space-y-3">
          {[
            "Log every day — even off days. Recovery days are data too.",
            "Be honest. The system can only protect a body it can see.",
            "Never skip the warm-up checklist on high-load days.",
            "Tell your AT through the app the moment something feels off — early flags end in treatment, late ones end in rehab.",
            "Check your trends weekly. Athletes who watch their own data improve faster — it's your career record, own it.",
          ].map((r) => (
            <div key={r} className="flex gap-3 items-start text-sm text-white/75">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {r}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-20 text-center">
        <div className="text-white/50 text-sm mb-4">One athlete. One record. Every rep counts.</div>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/login"><Button className="bg-cyan-400 text-[#0b1424] font-black px-6 py-6 hover:bg-cyan-300" data-testid="guide-portal-login-btn">Open my Portal <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          <Link to="/fusion/login"><Button variant="ghost" className="border border-white/20 text-white font-bold px-6 py-6 hover:bg-white/10" data-testid="guide-fusion-login-btn"><Flame className="mr-2 h-4 w-4 text-amber-300" /> Fusion sign in</Button></Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
